import { Button, IconNode, ToolTip, Typography } from '@components/base';
import { PeopleType, USER_TYPES } from '@helpers/constants';
import notify from '@helpers/toastify-helper';
import { IPeople, IPeopleStates } from '@helpers/types/people';
import { deletePoc, getPocs } from '@services/order.service';
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import UserCard from './components/user-card';
import css from './index.module.scss';
import { LEADERSHIP_EMAIL, SUPPORT_EMAIL, getPermissionFromLocalStorage } from '@helpers/utils';
import _ from 'lodash';
import AccessWrapper from '@authorization/access-wrapper';
import PeopleSkeleton from './skeleton';
import { DeletePrompt } from '@components/common';
import Images from '@assets/images';
import AddPOCModal from './components/add-poc';

const OrderPeopleTab = () => {
  const params = useParams();
  const [peopleState, setPeopleState] = useState<IPeopleStates>({
    peopleList: [],
    isLoading: false,
    addPOC: false,
    deletePOC: false,
    orderPocId: '',
    pocId: '',
    editMode: false,
    pocData: null
  });

  const { peopleList, isLoading, addPOC, editMode, pocData, deletePOC, pocId, orderPocId } =
    peopleState;
  const userType = useMemo(() => getPermissionFromLocalStorage()?.user_type, []);

  const fetchPeopleData = async () => {
    setPeopleState((prevState) => ({ ...prevState, isLoading: true }));

    try {
      const response = await getPocs(params.id as string);

      if (response.success) {
        let peopleData: any = [];

        if (userType == USER_TYPES.customer) {
          response?.data?.forEach((val: any) => {
            const sameTeam = peopleData?.find((x: any) => x.internal_team === val.internal_team);
            if (sameTeam) {
              sameTeam.user = _.uniqBy([...sameTeam.user, ...val.user], (u: any) => u.user_id);
            } else {
              peopleData.push(val);
            }
          });
        } else {
          peopleData = response.data;
        }

        setPeopleState((prevState) => ({
          ...prevState,
          peopleList: peopleData
        }));
      } else {
        notify({
          message: response?.error ?? 'Unable to fetch data',
          severity: 'error',
          dismissible: true
        });
      }
    } catch (error) {
      notify({
        message: 'An error occurred while fetching data',
        severity: 'error',
        dismissible: true
      });
    } finally {
      setPeopleState((prevState) => ({ ...prevState, isLoading: false }));
    }
  };

  useEffect(() => {
    fetchPeopleData();
  }, []);

  const toggleEditMode = () => {
    setPeopleState((prevState) => ({
      ...prevState,
      editMode: !prevState.editMode
    }));
  };

  const handleDeletePOC = async () => {
    const reqBody = {
      user_id: pocId
    };

    setPeopleState((prevState) => ({ ...prevState, isLoading: true }));
    const response = await deletePoc(orderPocId, reqBody);
    if (response.success) {
      notify({
        message: 'POC deleted successfully',
        severity: 'success',
        dismissible: true
      });
      fetchPeopleData();
    } else {
      notify({
        message: response?.error ?? 'Unable to delete POC',
        severity: 'error',
        dismissible: true
      });
    }
    setPeopleState((prevState) => ({
      ...prevState,
      isLoading: false,
      deletePOC: false,
      orderPocId: '',
      pocId: ''
    }));
  };

  const handleCancelDelete = () => {
    setPeopleState((prevState) => ({
      ...prevState,
      deletePOC: false,
      orderPocId: '',
      pocId: ''
    }));
  };

  const onAddPocClick = (val: IPeople) => {
    const orderPocId = val.order_poc_id;
    setPeopleState((prevState) => ({
      ...prevState,
      addPOC: true,
      orderPocId: orderPocId,
      pocData: val
    }));
  };

  const onDeleteUserClick = (userId: string, orderPocId: string) => {
    setPeopleState((prevState) => ({
      ...prevState,
      deletePOC: true,
      pocId: userId,
      orderPocId: orderPocId
    }));
  };

  const onAddPocFormSubmit = () => {
    setPeopleState((prevState) => ({
      ...prevState,
      addPOC: false,
      orderPocId: '',
      pocData: null
    }));
    fetchPeopleData();
  };

  if (isLoading) return <PeopleSkeleton />;

  return (
    <div className={css.contentWrapper}>
      <AccessWrapper show={true} showTo={[USER_TYPES.internal]} byPassShowWithShowTo>
        <div className={css.editButtonWrapper}>
          <Button variant="outlined-secondary" onClick={toggleEditMode}>
            {editMode ? 'Close Edit' : 'Edit'}
          </Button>
        </div>
      </AccessWrapper>

      <AccessWrapper show={true} showTo={[USER_TYPES.customer]} byPassShowWithShowTo>
        <div className={css.message}>
          <Typography variant="span" className={css.messageText}>
            If you face any issues please contact{' '}
            <a href={`mailto:${SUPPORT_EMAIL}`} className={css.emailLink}>
              {SUPPORT_EMAIL}
            </a>
            . Incase of any escalations please reach out to{' '}
            <a href={`mailto:${LEADERSHIP_EMAIL}`} className={css.emailLink}>
              {LEADERSHIP_EMAIL}
            </a>
          </Typography>
        </div>
      </AccessWrapper>

      {peopleList?.map((val, index) => {
        return (
          <React.Fragment key={index}>
            <div className={css.titleWrapper}>
              <Typography variant="h4" className={css.primaryTitle}>
                {val.internal_team}
                <AccessWrapper show={true} showTo={[USER_TYPES.internal]} byPassShowWithShowTo>
                  {editMode && (
                    <ToolTip title={`Add ${val.internal_team} ${PeopleType(val?.poc_type)}`}>
                      <div>
                        <IconNode
                          src={Images.duplicateRed}
                          alt="Add POC"
                          onClick={() => {
                            onAddPocClick(val);
                          }}
                        />
                      </div>
                    </ToolTip>
                  )}
                </AccessWrapper>
              </Typography>
              <AccessWrapper show={true} showTo={[USER_TYPES.internal]} byPassShowWithShowTo>
                <Typography variant="p" className={css.secondaryText}>
                  {PeopleType(val?.poc_type)}
                </Typography>
              </AccessWrapper>
            </div>
            <div className={css.textWrapper} key={index}>
              <UserCard
                userInfo={val?.user ?? []}
                orderPocId={val.order_poc_id}
                isEditMode={editMode}
                onDelete={(userId, pocId) => onDeleteUserClick(userId, pocId)}
              />
            </div>
          </React.Fragment>
        );
      })}

      {addPOC && (
        <AddPOCModal
          open={addPOC}
          onClose={() => {
            setPeopleState((prevState) => ({
              ...prevState,
              addPOC: false,
              orderPocId: '',
              pocData: null
            }));
          }}
          pocData={pocData}
          onAddPocFormSubmit={onAddPocFormSubmit}
        />
      )}

      <DeletePrompt
        open={!!(deletePOC && pocId && orderPocId)}
        onClose={handleCancelDelete}
        onConfirm={handleDeletePOC}
        message="Are you sure you want to delete this POC?"
      />
    </div>
  );
};

export default OrderPeopleTab;
